// pages/DriverOnboarding.js
// 4-step driver onboarding: personal info, vehicle registration, review, agreement.
// Standalone — no imports from BookingFlow or customer-facing components.
// Original IP — LoadDash Services LLC

import React, { useState, useEffect } from "react";
import { supabase } from "../services/supabaseService";
import DriverAgreement from "../legal/DriverAgreement";
import { TRUCK_MAX_LEU_TABLE } from "../dispatch/truckCapacityPolicy";

// ── Brand tokens (self-contained — no import from constants.js) ─────────
const NAVY = "#0a1628";
const NAVY2 = "#0d1b2e";
const BLUE = "#0487D9";
const GREEN = "#03A66A";
const RED = "#e53935";
const STEEL = "#607d9f";
const MUTED = "#8fa5be";
const BORDER = "#dce4ef";
const BG = "#f4f6f9";

const SORA = `@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&display=swap');`;

// ── Styles ──────────────────────────────────────────────────────────────
const card = { background: "#fff", borderRadius: 14, border: `1px solid ${BORDER}`, padding: "20px 18px", marginBottom: 14 };
const label = { fontSize: 11, fontWeight: 700, color: NAVY, marginBottom: 5, display: "block" };
const input = { width: "100%", background: BG, border: `1.5px solid ${BORDER}`, borderRadius: 10, color: NAVY, fontSize: 14, padding: "12px 14px", outline: "none", fontFamily: "inherit", boxSizing: "border-box" };
const btnPrimary = { width: "100%", padding: 15, borderRadius: 12, border: "none", background: `linear-gradient(135deg, #1565c0, #1976d2)`, color: "#fff", fontWeight: 800, fontSize: 15, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 4px 16px rgba(21,101,192,.35)" };
const btnDisabled = { ...btnPrimary, background: "#b0bec5", boxShadow: "none", cursor: "not-allowed" };
const btnSecondary = { padding: "12px 20px", borderRadius: 10, border: `1.5px solid ${BORDER}`, background: "#fff", color: STEEL, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" };
const errStyle = { fontSize: 12, color: RED, marginTop: 4 };

// ── VIN Decoding Database ───────────────────────────────────────────────
// WMI (first 3 chars) → make
const WMI_MAKE = {
  "1FT": "Ford", "3FT": "Ford", "1FM": "Ford",
  "1GC": "Chevrolet", "3GC": "Chevrolet", "1GT": "GMC", "3GT": "GMC",
  "1C6": "Ram", "3C6": "Ram", "1D7": "Ram", "3D7": "Ram",
  "3TM": "Toyota", "5TF": "Toyota", "5TB": "Toyota", "5TE": "Toyota",
  "1N6": "Nissan", "5N1": "Nissan",
  "5J6": "Honda",
  "1C4": "Jeep",
  "5NM": "Hyundai",
};

// Position 10 → model year (2010-2030 coverage)
const YEAR_CHAR = {
  A: "2010", B: "2011", C: "2012", D: "2013", E: "2014",
  F: "2015", G: "2016", H: "2017", J: "2018", K: "2019",
  L: "2020", M: "2021", N: "2022", P: "2023", R: "2024",
  S: "2025", T: "2026", V: "2027", W: "2028", X: "2029", Y: "2030",
};

// VIN prefix → model detection (verified against NHTSA VIN standard)
function detectModel(v, make) {
  // Ford
  if (v.startsWith('1FTFW') || v.startsWith('2FTFW')) return 'F-150';
  if (v.startsWith('1FTFX') || v.startsWith('2FTFX')) return 'F-250';
  if (v.startsWith('1FTFY') || v.startsWith('1FT8W')) return 'F-350';
  if (v.startsWith('1FTEX') || v.startsWith('1FTER') || v.startsWith('3FTTE')) return 'Ranger';
  if (v.startsWith('3FTTW') || v.startsWith('3FTTE')) return 'Maverick';
  // Chevrolet
  if (v.startsWith('1GC1K') || v.startsWith('1GC2K') || v.startsWith('1GC0K')) return 'Silverado 1500';
  if (v.startsWith('1GC4K') || v.startsWith('1GC3K') || v.startsWith('1GC1Y')) return 'Silverado 2500HD';
  if (v.startsWith('1GC5K') || v.startsWith('1GC4Y')) return 'Silverado 3500HD';
  if (v.startsWith('1GC') && make === 'Chevrolet') return 'Silverado 1500';
  if (v.startsWith('1GCS') || v.startsWith('2GCS')) return 'Colorado';
  // GMC
  if (v.startsWith('1GT0K') || v.startsWith('1GT1K') || v.startsWith('1GT2K')) return 'Sierra 1500';
  if (v.startsWith('1GT4K') || v.startsWith('1GT3K')) return 'Sierra 2500HD';
  if (v.startsWith('1GT5K') || v.startsWith('1GT4Y')) return 'Sierra 3500HD';
  if (v.startsWith('1GT') && make === 'GMC') return 'Sierra 1500';
  if (v.startsWith('1GTS') || v.startsWith('2GTS')) return 'Canyon';
  // Jeep
  if (v.startsWith('1C4HJT') || v.startsWith('1C4HJX')) return 'Gladiator';
  // Ram
  if (v.startsWith('1C6RR') || v.startsWith('1C6SR') || v.startsWith('3C6R') || v.startsWith('3C63')) return 'Ram 1500';
  if (v.startsWith('1C6SS') || v.startsWith('3C6S') || v.startsWith('3C64')) return 'Ram 2500';
  if (v.startsWith('1C6T') || v.startsWith('3C6T') || v.startsWith('3C65')) return 'Ram 3500';
  if (v.startsWith('1C6') || v.startsWith('3C6')) return 'Ram 1500';
  // Hyundai
  if (v.startsWith('5NMS3') || v.startsWith('5NMS4')) return 'Santa Cruz';
  // Toyota — specific prefixes before generic catch-alls
  if (v.startsWith('5TFDW') || v.startsWith('5TFAW') ||
      v.startsWith('5TFJX') || v.startsWith('5TFRY') || v.startsWith('5TFRM') ||
      v.startsWith('5TFEM') || v.startsWith('5TFHM') || v.startsWith('5TFUM') ||
      v.startsWith('5TFDM') || v.startsWith('5TFBM') || v.startsWith('5TFCM') ||
      v.startsWith('5TFDY') || v.startsWith('5TFWY')) return 'Tundra';
  if (v.startsWith('5TFRX') || v.startsWith('5TFAZ') ||
      v.startsWith('5TFSX') || v.startsWith('5TFRZ') ||
      v.startsWith('5TFCZ') || v.startsWith('5TFDZ')) return 'Tacoma';
  if (v.startsWith('3TMC') || v.startsWith('3TMM') || v.startsWith('3TMK') ||
      v.startsWith('3TML') || v.startsWith('3TMR') || v.startsWith('3TM')) return 'Tacoma';
  if (v.startsWith('5TF') || v.startsWith('5TD')) return 'Tundra';
  // Nissan
  if (v.startsWith('1N6AD') || v.startsWith('1N6BD') || v.startsWith('3N1CD')) return 'Frontier';
  if (v.startsWith('1N6AA') || v.startsWith('1N6BA') || v.startsWith('5N1AA')) return 'Titan';
  if (v.startsWith('1N6')) return 'Titan';
  // Honda
  if (v.startsWith('5FNR') || v.startsWith('2HKR')) return 'Ridgeline';
  return null;
}

// Verified bed dimensions (inside box L × inside box W at rail, inches) — Raybuck data
const BED_DB = {
  'F-150':           [{ s:'short',    label:"5.5'", L:67,    W:65     }, { s:'standard', label:"6.5'", L:78.9,  W:65     }, { s:'long', label:"8'", L:97,    W:65     }],
  'F-250':           [{ s:'standard', label:"6.75'", L:81.75, W:69    }, { s:'long',     label:"8'",   L:98,    W:69     }],
  'F-350':           [{ s:'standard', label:"6.75'", L:81.75, W:69    }, { s:'long',     label:"8'",   L:98,    W:69     }],
  'Ranger':          [{ s:'standard', label:"6'",    L:73.3,  W:61.5  }],
  'Maverick':        [{ s:'standard', label:"4.5'",  L:54.4,  W:57.6  }],
  'Silverado 1500':  [{ s:'short',    label:"5.8'",  L:69.9,  W:71.4  }, { s:'standard', label:"6.5'", L:79.4,  W:71.4  }, { s:'long', label:"8.2'", L:98.2,  W:71.4   }],
  'Silverado 2500HD':[{ s:'standard', label:"6.75'", L:82.3,  W:71.4  }, { s:'long',     label:"8.2'",  L:98.3,  W:71.4  }],
  'Sierra 1500':     [{ s:'short',    label:"5.8'",  L:69.92, W:71.4  }, { s:'standard', label:"6.5'", L:79.44, W:71.4  }, { s:'long', label:"8'", L:98.2,  W:71.4   }],
  'Sierra 2500HD':   [{ s:'standard', label:"6.75'", L:82.3,  W:71.4  }, { s:'long',     label:"8.2'",  L:98.3,  W:71.4  }],
  'Colorado':        [{ s:'short',    label:"5.2'",  L:61.7,  W:57    }, { s:'standard', label:"6.2'", L:74,    W:57    }],
  'Canyon':          [{ s:'standard', label:"5.2'",  L:61.7,  W:58.1  }],
  'Ram 1500':        [{ s:'short',    label:"5.7'",  L:67.4,  W:62    }, { s:'standard', label:"6.4'", L:76.3,  W:65.375}],
  'Ram 2500':        [{ s:'standard', label:"6.3'",  L:76.3,  W:66.4  }, { s:'long',     label:"8'",   L:98.3,  W:66.4  }],
  'Ram 3500':        [{ s:'standard', label:"6.3'",  L:76.3,  W:66.4  }, { s:'long',     label:"8'",   L:98.3,  W:66.4  }],
  'Tacoma':          [{ s:'short',    label:"5'",    L:60.3,  W:61.0  }, { s:'standard', label:"6'",   L:73.5,  W:61.0  }],
  'Tundra':          [{ s:'short',    label:"5.5'",  L:65.6,  W:63.0  }, { s:'standard', label:"6.5'", L:77.6,  W:63.0  }, { s:'long', label:"8.1'", L:96.5,  W:63.0  }],
  'Frontier':        [{ s:'short',    label:"5'",    L:59,    W:61.4  }, { s:'standard', label:"6'",   L:73.3,  W:61.4  }],
  'Titan':           [{ s:'short',    label:"5.5'",  L:65.4,  W:63.8  }, { s:'standard', label:"6.5'", L:77.1,  W:63.8  }, { s:'long', label:"8'", L:78.7,  W:63.8   }],
  'Ridgeline':       [{ s:'standard', label:"5.4'",  L:64,    W:60    }],
  'Gladiator':       [{ s:'standard', label:"5'",    L:60,    W:57.8  }],
  'Santa Cruz':      [{ s:'standard', label:"4.3'",  L:52.1,  W:53.0  }],
  'Silverado 3500HD':[{ s:'standard', label:"6.75'", L:82.3,  W:71.4  }, { s:'long', label:"8.2'", L:98.3,  W:71.4  }],
  'Sierra 3500HD':   [{ s:'standard', label:"6.75'", L:82.3,  W:71.4  }, { s:'long', label:"8.2'", L:98.3,  W:71.4  }],
};

// ── Wheelwell-to-wheelwell width lookup (from TRUCK_LEU_CAPACITY.md) ───
// WW width is the physically usable floor width between wheel wells.
// This is the authoritative width for LEU capacity calculation.
// BED_DB widths above are at-rail (display only). WW widths below are for LEU math.
const WW_WIDTH = {
  'F-150':           { short: 50.6, standard: 50.6, long: 50.6 },
  'F-250':           { standard: 66.9, long: 66.9 },
  'F-350':           { standard: 66.9, long: 66.9 },
  'Ranger':          { short: 44, standard: 44 },
  'Maverick':        { standard: 0 }, // Disqualified — not eligible
  'Silverado 1500':  { short: 50.6, standard: 50.6, long: 50.6 },
  'Silverado 2500HD':{ standard: 54, long: 54 },
  'Silverado 3500HD':{ standard: 54, long: 54 },
  'Sierra 1500':     { short: 50.6, standard: 50.6, long: 50.6 },
  'Sierra 2500HD':   { standard: 54, long: 54 },
  'Sierra 3500HD':   { standard: 54, long: 54 },
  'Ram 1500':        { short: 51, standard: 51 },
  'Ram 2500':        { standard: 51, long: 51 },
  'Ram 3500':        { standard: 51, long: 51 },
  'Tacoma':          { short: 44.7, standard: 44.7 },
  'Tundra':          { short: 58.7, standard: 58.7, long: 58.7 },
  'Frontier':        { short: 44.5, standard: 44.5 },
  'Titan':           { short: 50, standard: 50, long: 50 },
  'Ridgeline':       { standard: 50 },
  'Colorado':        { short: 50, standard: 50 },
  'Canyon':          { standard: 50 },
  'Gladiator':       { standard: 50 },
  'Santa Cruz':      { standard: 48.4 },
};

// Look up wheelwell width for a model + bed size combination.
// Returns null if model/bed is not in the reference table.
function getWWWidth(model, bedSizeKey) {
  if (!model) return null;
  const entry = WW_WIDTH[model];
  if (!entry) return null;
  return entry[bedSizeKey] ?? entry.standard ?? null;
}

function decodeVIN(vin) {
  if (!vin || vin.length !== 17) return null;
  const wmi = vin.slice(0, 3);
  const make = WMI_MAKE[wmi] || null;
  const yearChar = vin[9];
  const year = YEAR_CHAR[yearChar] || null;
  const model = detectModel(vin, make);
  return { make, year, model };
}

// Compute max LEU from the authoritative reference table in truckCapacityPolicy.js.
// The table maps model + bed size → exact LEU from TRUCK_LEU_CAPACITY.md.
// Fallback: floor(L * WW / 400) capped at 10 for unknown models with manual dimensions.
// Source: TRUCK_LEU_CAPACITY.md
function computeMaxLEU(model, bedSizeKey, bedLength, wwWidth) {
  // Primary: authoritative table lookup
  if (model && model in TRUCK_MAX_LEU_TABLE) {
    const entry = TRUCK_MAX_LEU_TABLE[model];
    if (entry === null) return 0; // Disqualified (Maverick)
    const val = entry[bedSizeKey] ?? entry.standard ?? null;
    if (val != null) return val;
  }
  // Fallback: floor footprint formula for unknown models with manual dimensions
  if (!bedLength || !wwWidth || bedLength <= 0 || wwWidth <= 0) return null;
  return Math.min(10, Math.floor(bedLength * wwWidth / 400));
}

function formatPhone(raw) {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function phoneDigits(formatted) {
  return formatted.replace(/\D/g, "");
}

// ═══════════════════════════════════════════════════════════════════════
export default function DriverOnboarding({ session, existingProfile, onComplete }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Step 1 — Personal
  const meta = session?.user?.user_metadata || {};
  const nameParts = (meta.name || "").trim().split(/\s+/);
  const [firstName, setFirstName] = useState(existingProfile?.first_name || nameParts[0] || "");
  const [lastName, setLastName] = useState(existingProfile?.last_name || nameParts.slice(1).join(" ") || "");
  const [phone, setPhone] = useState(existingProfile?.phone || "");

  // Step 2 — Vehicle
  const [vin, setVin] = useState(existingProfile?.vin || "");
  const [decoded, setDecoded] = useState(null);
  const [bedChoice, setBedChoice] = useState(null); // index into BED_DB[model]
  const [manualL, setManualL] = useState(existingProfile?.bed_l_in?.toString() || "");
  const [manualW, setManualW] = useState(existingProfile?.bed_w_in?.toString() || "");

  // Decode VIN when it reaches 17 chars
  useEffect(() => {
    const clean = vin.toUpperCase().replace(/[IOQ]/g, "").slice(0, 17);
    if (clean !== vin) setVin(clean);
    if (clean.length === 17) {
      const d = decodeVIN(clean);
      setDecoded(d);
      setBedChoice(null);
    } else {
      setDecoded(null);
      setBedChoice(null);
    }
  }, [vin]);

  // Derived
  const beds = decoded?.model ? BED_DB[decoded.model] || null : null;
  const selectedBed = beds && bedChoice != null ? beds[bedChoice] : null;
  const bedL = selectedBed ? selectedBed.L : parseFloat(manualL) || 0;
  const bedW = selectedBed ? selectedBed.W : parseFloat(manualW) || 0; // at-rail width (display only)
  const bedSizeKey = selectedBed?.s || (bedL > 0 && bedW > 0 ? "custom" : null);
  const wwWidth = getWWWidth(decoded?.model, bedSizeKey) ?? (parseFloat(manualW) || 0); // WW width for LEU math
  const maxLEU = computeMaxLEU(decoded?.model, bedSizeKey, bedL, wwWidth);
  const bedLabel = selectedBed ? `${decoded?.model} ${selectedBed.label}` : (bedL > 0 && bedW > 0 ? `Custom ${bedL}" x ${bedW}"` : "");

  // Capacity visualization (driver-facing — no LEU language)
  // Uses WW-based footprint: maxLEU / 10 * 100 for percentage (10 LEU = 100%)
  const capacityPct = maxLEU != null ? Math.min(100, Math.round(maxLEU / 10 * 100)) : 0;
  const capacityColor = capacityPct <= 40 ? "#3D90D9" : capacityPct <= 70 ? "#F29727" : "#1D9E75";
  const capacityLabel = capacityPct <= 33 ? "Light jobs — bags, small items"
    : capacityPct <= 55 ? "Medium jobs — furniture, bulk bags"
    : capacityPct <= 79 ? "Large jobs — full room cleanouts"
    : "Max capacity — heavy full loads";

  // Validation
  const isMaverickBlocked = decoded?.model?.toLowerCase() === "maverick";
  const step1Valid = firstName.trim() && lastName.trim() && phoneDigits(phone).length === 10;
  const step2Valid = vin.length === 17 && bedSizeKey && bedL > 0 && bedW > 0 && !isMaverickBlocked;

  async function handleSaveAndContinue() {
    setSaving(true);
    setSaveError("");
    try {
      const payload = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: "+1" + phoneDigits(phone),
        market_id: "ut-hill-afb",
        vin,
        vehicle_year: decoded?.year || null,
        vehicle_make: decoded?.make || null,
        vehicle_model: decoded?.model || null,
        bed_size: bedSizeKey,
        bed_label: bedLabel,
        bed_l_in: bedL.toString(),
        bed_w_in: bedW.toString(),
        max_leu: maxLEU?.toString() || null,
        onboarding_status: "incomplete",
      };
      const { error } = await supabase.rpc("upsert_driver_profile", { payload });
      if (error) throw error;
      setStep(4);
    } catch (e) {
      setSaveError(e.message || "Save failed");
    }
    setSaving(false);
  }

  // ── Step 1: Personal ──────────────────────────────────────────────────
  function renderStep1() {
    return (
      <div style={card}>
        <div style={{ fontWeight: 800, fontSize: 16, color: NAVY, marginBottom: 16 }}>Personal Information</div>

        <div style={{ marginBottom: 14 }}>
          <label style={label}>First Name</label>
          <input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name" style={input} />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={label}>Last Name</label>
          <input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name" style={input} />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={label}>Phone Number</label>
          <input value={formatPhone(phone)} onChange={e => setPhone(phoneDigits(e.target.value))} placeholder="(801) 555-0123" type="tel" style={input} />
          {phone && phoneDigits(phone).length !== 10 && phoneDigits(phone).length > 0 && (
            <div style={errStyle}>Enter a 10-digit US phone number</div>
          )}
        </div>

        <button onClick={() => setStep(2)} disabled={!step1Valid} style={step1Valid ? btnPrimary : btnDisabled}>
          Next: Vehicle Registration
        </button>
      </div>
    );
  }

  // ── Step 2: Vehicle ───────────────────────────────────────────────────
  function renderStep2() {
    return (
      <>
        <div style={card}>
          <div style={{ fontWeight: 800, fontSize: 16, color: NAVY, marginBottom: 16 }}>Vehicle Registration</div>

          <div style={{ marginBottom: 14 }}>
            <label style={label}>VIN (17 characters)</label>
            <input value={vin} onChange={e => setVin(e.target.value.toUpperCase().replace(/[IOQ]/g, "").slice(0, 17))} placeholder="1FTEW1E5XRFA00000" style={{ ...input, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 1.5, fontSize: 15 }} maxLength={17} />
            <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>{vin.length}/17 characters</div>
          </div>

          {decoded && (
            <div style={{ background: decoded.make ? "#e8f5e9" : "#fff8e1", border: `1px solid ${decoded.make ? "#a5d6a7" : "#fcd34d"}`, borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: decoded.make ? GREEN : "#b45309", marginBottom: 6 }}>
                {decoded.make ? "VIN Decoded" : "Partial Decode"}
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>
                {decoded.year || "????"} {decoded.make || "Unknown Make"} {decoded.model || "Unknown Model"}
              </div>
              {isMaverickBlocked && <div style={{ fontSize: 12, fontWeight: 700, color: RED, marginTop: 6 }}>The Ford Maverick bed is too short for residential pickup items. This vehicle is not eligible for operator registration.</div>}
              {!decoded.model && !isMaverickBlocked && <div style={{ fontSize: 11, color: STEEL, marginTop: 4 }}>Model not in database. Enter bed dimensions manually below.</div>}
            </div>
          )}

          {/* Bed size selector for known models */}
          {beds && (
            <div style={{ marginBottom: 14 }}>
              <label style={label}>Bed Size</label>
              <div style={{ display: "flex", gap: 8 }}>
                {beds.map((b, i) => (
                  <button key={b.key} onClick={() => setBedChoice(i)} style={{
                    flex: 1, padding: "12px 8px", borderRadius: 10,
                    border: bedChoice === i ? `2px solid ${BLUE}` : `1.5px solid ${BORDER}`,
                    background: bedChoice === i ? "#e3f2fd" : "#fff",
                    cursor: "pointer", fontFamily: "inherit",
                  }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: bedChoice === i ? BLUE : NAVY }}>{b.label}</div>
                    <div style={{ fontSize: 10, color: STEEL, marginTop: 2 }}>{b.L}" x {b.W}"</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Manual dimensions for unknown models */}
          {decoded && !beds && (
            <div style={{ marginBottom: 14 }}>
              <label style={label}>Bed Interior Dimensions (inches)</label>
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <input value={manualL} onChange={e => setManualL(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="Length" type="text" inputMode="decimal" style={input} />
                  <div style={{ fontSize: 10, color: MUTED, marginTop: 2 }}>Length (in)</div>
                </div>
                <div style={{ flex: 1 }}>
                  <input value={manualW} onChange={e => setManualW(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="Width" type="text" inputMode="decimal" style={input} />
                  <div style={{ fontSize: 10, color: MUTED, marginTop: 2 }}>Width (in)</div>
                </div>
              </div>
            </div>
          )}

          {/* Truck bed capacity visualization */}
          {capacityPct > 0 && (
            <div style={{ background: NAVY, borderRadius: 12, padding: "16px 18px", marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: "#42a5f5", marginBottom: 10, textAlign: "center" }}>Your Truck Bed</div>
              <svg viewBox="0 0 300 80" style={{ width: "100%", height: 80, display: "block" }}>
                {/* Bed outline */}
                <rect x="4" y="4" width="292" height="72" rx="8" ry="8" fill="#0d1b2e" stroke="#1e3558" strokeWidth="2" />
                {/* Capacity fill — rises from bottom */}
                <clipPath id="bed-clip"><rect x="6" y="6" width="288" height="68" rx="6" ry="6" /></clipPath>
                <rect x="6" y={6 + 68 * (1 - capacityPct / 100)} width="288" height={68 * capacityPct / 100} fill={capacityColor} opacity="0.35" clipPath="url(#bed-clip)" />
                {/* Percentage label */}
                <text x="150" y="46" textAnchor="middle" fill="#fff" fontSize="22" fontWeight="800" fontFamily="'Sora',system-ui,sans-serif">{capacityPct}%</text>
              </svg>
              <div style={{ fontSize: 13, fontWeight: 700, color: capacityColor, textAlign: "center", marginTop: 8 }}>{capacityLabel}</div>
              <div style={{ fontSize: 10, color: MUTED, textAlign: "center", marginTop: 4 }}>{bedL}" L x {bedW}" W</div>
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => setStep(1)} style={btnSecondary}>Back</button>
          <button onClick={() => setStep(3)} disabled={!step2Valid} style={{ flex: 1, ...(step2Valid ? btnPrimary : btnDisabled) }}>
            Next: Review
          </button>
        </div>
      </>
    );
  }

  // ── Step 3: Review ────────────────────────────────────────────────────
  function renderStep3() {
    const rows = [
      ["Name", `${firstName} ${lastName}`],
      ["Phone", formatPhone(phone)],
      ["VIN", vin],
      ["Vehicle", `${decoded?.year || "—"} ${decoded?.make || "—"} ${decoded?.model || "—"}`],
      ["Bed", bedLabel || "—"],
      ["Dimensions", bedL && bedW ? `${bedL}" L x ${bedW}" W` : "—"],
      ["Job capacity", capacityPct > 0 ? capacityLabel : "—"],
    ];

    return (
      <>
        <div style={card}>
          <div style={{ fontWeight: 800, fontSize: 16, color: NAVY, marginBottom: 16 }}>Review Your Profile</div>
          {rows.map(([l, v]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: `1px solid ${BORDER}` }}>
              <span style={{ fontSize: 13, color: STEEL, fontWeight: 600 }}>{l}</span>
              <span style={{ fontSize: 13, color: NAVY, fontWeight: 700, textAlign: "right", maxWidth: "60%", wordBreak: "break-all" }}>{v}</span>
            </div>
          ))}
        </div>

        {saveError && (
          <div style={{ background: "#fce4e4", border: "1px solid #ef9a9a", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: RED, marginBottom: 14 }}>{saveError}</div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => setStep(2)} style={btnSecondary}>Back</button>
          <button onClick={handleSaveAndContinue} disabled={saving} style={{ flex: 1, ...(saving ? btnDisabled : btnPrimary) }}>
            {saving ? "Saving..." : "Save and Continue"}
          </button>
        </div>
      </>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: BG, fontFamily: "'Sora',system-ui,sans-serif" }}>
      <style>{SORA}</style>

      {/* Header */}
      <div style={{ background: `linear-gradient(160deg, #060e1a, ${NAVY2})`, padding: "20px 18px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <img src="/Loaddashbutton.png" alt="LD" style={{ width: 28, height: 28, borderRadius: 7, objectFit: "contain" }} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: "#42a5f5" }}>LoadDash Loaders</span>
        </div>
        <h2 style={{ fontWeight: 900, fontSize: 22, color: "#fff", margin: 0, letterSpacing: -0.4 }}>Loader Setup</h2>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,.35)", marginTop: 4 }}>Step {step} of 4</div>
      </div>

      {/* Progress dots */}
      <div style={{ display: "flex", gap: 6, padding: "14px 18px" }}>
        {[1, 2, 3, 4].map(s => (
          <div key={s} style={{ flex: 1, height: 3, borderRadius: 2, background: s <= step ? BLUE : BORDER }} />
        ))}
      </div>

      {/* Content */}
      {step <= 3 && (
        <div style={{ padding: "4px 18px 80px" }}>
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>
      )}
      {step === 4 && (
        <DriverAgreement session={session} onComplete={onComplete} />
      )}
    </div>
  );
}
